/**
 * IndexedDB-backed photo upload queue for the contributor capture flow.
 * See docs/contributor-capture-and-review-spec.md §5.3.
 *
 * Lifecycle of a queued photo:
 *
 *   captured  →  pending_upload  →  uploading  →  uploaded
 *                       ↑               ↓            ↓
 *                       └──── upload_failed      purged (bytes dropped on
 *                        (transient — retried     markUploaded; row deleted
 *                         on next drain, up to    by purgeUploaded() after
 *                         the drain's attempt     UPLOADED_ROW_TTL)
 *                         cap)
 *
 * `uploading` is a CLAIM: `claimForUpload()` flips a row to it atomically
 * (single readwrite transaction) so two concurrent drains — reconnect
 * listener vs. page mutex, or two open tabs sharing this DB — can never
 * both upload the same row. A claim older than STALE_CLAIM_MS (tab killed
 * mid-upload) is reclaimable.
 *
 * The queue survives reloads, tab closes, and offline gaps. A captured
 * photo with no connectivity stays in `pending_upload` until the next
 * `online` event triggers a drain.
 *
 * One IndexedDB store, one ObjectStore (`queue`). Keys are auto-generated
 * UUIDs. This queue is the "outbox", not the "history": markUploaded()
 * drops the (multi-MB) photo bytes immediately so quota-constrained
 * targets (iOS Safari PWA) never accumulate dead ArrayBuffers, and
 * purgeUploaded() deletes stale uploaded rows entirely.
 */

import type { Containment, Obstacle, Slope, Surface } from './types';

const DB_NAME = 'trash-trail-upload-queue';
const STORE = 'queue';
const DB_VERSION = 1;

export type UploadStatus =
  | 'pending_upload'
  | 'uploading'
  | 'uploaded'
  | 'upload_failed';

/**
 * An `uploading` claim older than this is treated as abandoned (the tab
 * was killed mid-upload) and may be re-claimed by the next drain. Must
 * comfortably exceed the 60s per-photo xhr timeout in api.uploadPhoto.
 */
export const STALE_CLAIM_MS = 5 * 60_000;

/**
 * Optional per-SESSION reachability tags (V1 contract §6). Only fields the
 * user explicitly answered in the "Help cleanup robots reach this"
 * disclosure are present — absent means unknown, and NOTHING is fabricated
 * on their behalf. The same tags ride along with every photo's submission
 * in the session.
 */
export interface SessionTags {
  surface?: Surface;
  containment?: Containment;
  slope?: Slope;
  /** `[]` = "user said none"; absent = unknown. */
  obstacles_on_path?: Obstacle[];
}

export interface QueuedPhoto {
  /** Auto-generated UUID, the IndexedDB key. */
  id: string;
  /** Groups all photos taken in one capture session. */
  sessionId: string;
  /** Stable per-photo ID (UUID, used as the S3 key suffix). */
  photoId: string;
  /** Client-side ISO 8601 capture timestamp. */
  capturedAt: string;
  /**
   * Image bytes. Stored as ArrayBuffer + mime type instead of a Blob:
   * WebKit's IndexedDB (Playwright's build, plus older iOS Safari — the
   * PWA's primary target) aborts the transaction with "Error preparing
   * Blob/File data to be stored in object store" on Blob/File values.
   * ArrayBuffers structured-clone everywhere.
   */
  bytes: ArrayBuffer;
  /** MIME type of `bytes` (from the original File; may be ''). */
  blobType: string;
  /** Best-effort GPS, captured in parallel with the shutter. */
  geo?: { lat: number; lon: number; accuracy: number };
  /** Optional shell-provided label ("Marvell Park cleanup"). */
  sessionNote?: string;
  /** Optional per-session reachability tags — only user-answered fields. */
  tags?: SessionTags;
  /** Current state. */
  status: UploadStatus;
  /** ISO 8601 of the last transition. */
  updatedAt: string;
  /** Most recent error message (only when status='upload_failed'). */
  lastError?: string;
  /** Number of upload attempts so far. */
  attempts: number;
  /**
   * Submission ULID returned by POST /submissions for this row. Persisted
   * BEFORE the S3 PUT so a failure after the 201 retries the PUT against
   * the same submission instead of minting a duplicate (the backend has
   * no dedupe on client_photo_id).
   */
  submissionId?: string;
  /** Presigned S3 PUT URL from the 201, reused while unexpired. */
  uploadUrl?: string;
  /** ISO expiry of `uploadUrl` (the URL stops working after this). */
  uploadUrlExpiresAt?: string;
}

// Cache one open DB connection for the page lifetime. Reopening on
// every call to enqueue/listX trips the browser's IDB open-throttle
// and also makes deleteDatabase block indefinitely in tests because
// a stale open connection prevents the delete from progressing.
let cachedDb: IDBDatabase | null = null;
let cachedDbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (cachedDb) return Promise.resolve(cachedDb);
  if (cachedDbPromise) return cachedDbPromise;
  cachedDbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('sessionId', 'sessionId', { unique: false });
        store.createIndex('status', 'status', { unique: false });
      }
    };
    req.onsuccess = () => {
      cachedDb = req.result;
      // If the page version is bumped elsewhere, close so the next call
      // reopens against the new schema.
      cachedDb.onversionchange = () => {
        cachedDb?.close();
        cachedDb = null;
        cachedDbPromise = null;
      };
      resolve(req.result);
    };
    req.onerror = () => {
      cachedDbPromise = null;
      reject(req.error);
    };
  });
  return cachedDbPromise;
}

function awaitTx(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export interface EnqueueInput {
  sessionId: string;
  photoId: string;
  capturedAt: string;
  /** Original capture; converted to ArrayBuffer for storage. */
  blob: Blob;
  geo?: { lat: number; lon: number; accuracy: number };
  sessionNote?: string;
  tags?: SessionTags;
}

/** Add a photo to the queue in `pending_upload` state. */
export async function enqueue(input: EnqueueInput): Promise<QueuedPhoto> {
  const { blob, ...rest } = input;
  const item: QueuedPhoto = {
    id: randomId(),
    ...rest,
    bytes: await blob.arrayBuffer(),
    blobType: blob.type,
    status: 'pending_upload',
    updatedAt: new Date().toISOString(),
    attempts: 0,
  };
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  tx.objectStore(STORE).add(item);
  await awaitTx(tx);
  return item;
}

/**
 * Mark a photo as uploaded. The (multi-MB) photo bytes are dropped right
 * away — they're on S3 now and iOS Safari's origin quota is tight — but
 * the row itself stays so the /submit status bar can keep counting this
 * session; purgeUploaded() deletes stale uploaded rows later.
 */
export async function markUploaded(id: string): Promise<void> {
  await mutate(id, (row) => {
    row.status = 'uploaded';
    row.lastError = undefined;
    row.bytes = new ArrayBuffer(0);
    row.uploadUrl = undefined;
    row.uploadUrlExpiresAt = undefined;
    row.updatedAt = new Date().toISOString();
  });
}

/**
 * Atomically claim a row for upload: flips pending_upload / upload_failed
 * (or a stale abandoned `uploading` claim) to `uploading` and returns the
 * fresh row. Returns null when the row is gone, already uploaded, or
 * currently claimed by another drain/tab. The get-check-put runs inside
 * ONE readwrite transaction — IndexedDB serializes those per store, so
 * two racing claimants can never both win.
 */
export async function claimForUpload(
  id: string,
  now: number = Date.now(),
): Promise<QueuedPhoto | null> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);
  const claimed = await new Promise<QueuedPhoto | null>((resolve, reject) => {
    const get = store.get(id);
    get.onsuccess = () => {
      const row = get.result as QueuedPhoto | undefined;
      const claimable =
        row &&
        (row.status === 'pending_upload' ||
          row.status === 'upload_failed' ||
          (row.status === 'uploading' &&
            now - Date.parse(row.updatedAt) >= STALE_CLAIM_MS));
      if (!row || !claimable) {
        resolve(null);
        return;
      }
      row.status = 'uploading';
      row.updatedAt = new Date(now).toISOString();
      const put = store.put(row);
      put.onsuccess = () => resolve(row);
      put.onerror = () => reject(put.error);
    };
    get.onerror = () => reject(get.error);
  });
  await awaitTx(tx);
  return claimed;
}

/**
 * Persist the POST /submissions result on the row BEFORE the S3 PUT so a
 * failure (or tab death) after the 201 never re-creates the submission —
 * the retry resumes at the PUT with the same presigned URL.
 */
export async function recordUploadTarget(
  id: string,
  target: { submissionId: string; uploadUrl: string; uploadUrlExpiresAt: string },
): Promise<void> {
  await mutate(id, (row) => {
    row.submissionId = target.submissionId;
    row.uploadUrl = target.uploadUrl;
    row.uploadUrlExpiresAt = target.uploadUrlExpiresAt;
    row.updatedAt = new Date().toISOString();
  });
}

/**
 * Record a failed upload attempt; row stays in queue for retry.
 *
 * `attempts` is the auto-retry budget the drain loop reads. Pass
 * `stopAt` to spend it in one go for a failure that retrying cannot fix —
 * a licence refusal is the same answer every time, and burning five
 * attempts on it just delays the row reaching the state where the UI
 * explains itself. The manual Retry button still resets it, which is
 * exactly right: by then the contributor may have accepted.
 */
export async function markFailed(
  id: string,
  error: string,
  stopAt?: number,
): Promise<void> {
  await mutate(id, (row) => {
    row.status = 'upload_failed';
    row.lastError = error;
    row.attempts = stopAt ?? row.attempts + 1;
    row.updatedAt = new Date().toISOString();
  });
}

/** Reset a failed upload to pending so a retry will pick it up. */
export async function retry(id: string): Promise<void> {
  await mutate(id, (row) => {
    row.status = 'pending_upload';
    row.lastError = undefined;
    row.updatedAt = new Date().toISOString();
  });
}

/**
 * Best-effort update of the per-session metadata (reachability tags +
 * session note) on every NOT-YET-UPLOADED row of a session. Called when
 * the user edits the optional disclosure after photos were already
 * queued — photos that uploaded before the edit keep whatever was sent
 * (their canonical labels come from /review anyway).
 */
export async function updateSessionMeta(
  sessionId: string,
  meta: { tags?: SessionTags; sessionNote?: string },
): Promise<void> {
  const rows = await listSession(sessionId);
  for (const row of rows) {
    if (row.status === 'uploaded') continue;
    await mutate(row.id, (r) => {
      r.tags = meta.tags;
      r.sessionNote = meta.sessionNote;
      r.updatedAt = new Date().toISOString();
    });
  }
}

/** Delete a row unconditionally. */
export async function remove(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  tx.objectStore(STORE).delete(id);
  await awaitTx(tx);
}

/**
 * Delete a row only while its photo hasn't gone (or started going) to the
 * server: pending_upload / upload_failed rows are deleted, `uploading` and
 * `uploaded` rows are left alone. Used when the user removes a photo from
 * the /submit tray — a not-yet-uploaded photo must NOT upload anyway.
 * Atomic (one readwrite tx) so it can't race a concurrent claim.
 * Returns true when the row was deleted.
 */
export async function removeIfPending(id: string): Promise<boolean> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);
  const deleted = await new Promise<boolean>((resolve, reject) => {
    const get = store.get(id);
    get.onsuccess = () => {
      const row = get.result as QueuedPhoto | undefined;
      if (!row || (row.status !== 'pending_upload' && row.status !== 'upload_failed')) {
        resolve(false);
        return;
      }
      const del = store.delete(id);
      del.onsuccess = () => resolve(true);
      del.onerror = () => reject(del.error);
    };
    get.onerror = () => reject(get.error);
  });
  await awaitTx(tx);
  return deleted;
}

/**
 * Garbage-collect `uploaded` rows older than maxAgeMs. Uploaded rows only
 * exist so the current /submit session's status bar can count them; left
 * forever they'd pin dead metadata in a quota-constrained origin (the
 * bytes themselves are already dropped by markUploaded). Called
 * best-effort at the start of every drain.
 */
export async function purgeUploaded(
  maxAgeMs: number,
  now: number = Date.now(),
): Promise<number> {
  const all = await listAll();
  const stale = all.filter(
    (r) => r.status === 'uploaded' && now - Date.parse(r.updatedAt) >= maxAgeMs,
  );
  for (const row of stale) {
    await remove(row.id);
  }
  return stale.length;
}

/** Return all rows for a session, in insertion order (key is UUID so
 *  ordering is roughly chronological — capturedAt is the canonical sort). */
export async function listSession(sessionId: string): Promise<QueuedPhoto[]> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readonly');
  return new Promise((resolve, reject) => {
    const req = tx.objectStore(STORE).index('sessionId').getAll(sessionId);
    req.onsuccess = () => {
      const rows = (req.result as QueuedPhoto[]).slice();
      rows.sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
      resolve(rows);
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Return all not-yet-uploaded rows (`pending_upload`, `upload_failed`,
 * and in-flight/abandoned `uploading`) across all sessions. Drain
 * candidates only — actually working on a row still requires winning
 * `claimForUpload`.
 */
export async function listPending(): Promise<QueuedPhoto[]> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readonly');
  return new Promise((resolve, reject) => {
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => {
      const rows = (req.result as QueuedPhoto[]).filter(
        (r) => r.status !== 'uploaded',
      );
      rows.sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
      resolve(rows);
    };
    req.onerror = () => reject(req.error);
  });
}

/** Return all rows (any status). Used for UI status indicators. */
export async function listAll(): Promise<QueuedPhoto[]> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readonly');
  return new Promise((resolve, reject) => {
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result as QueuedPhoto[]);
    req.onerror = () => reject(req.error);
  });
}

async function mutate(id: string, fn: (row: QueuedPhoto) => void): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);
  await new Promise<void>((resolve, reject) => {
    const get = store.get(id);
    get.onsuccess = () => {
      const row = get.result as QueuedPhoto | undefined;
      if (!row) {
        // Row vanished between caller's listPending() and our mutate.
        // Not an error — caller may have purged a stale row.
        resolve();
        return;
      }
      fn(row);
      const put = store.put(row);
      put.onsuccess = () => resolve();
      put.onerror = () => reject(put.error);
    };
    get.onerror = () => reject(get.error);
  });
  await awaitTx(tx);
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Drop the entire queue database. Sign-out / account-deletion hygiene:
 * on a shared device the next user must not inherit the previous user's
 * captured-but-unuploaded photos.
 */
export async function deleteQueueDatabase(): Promise<void> {
  // Must close the cached connection BEFORE deleteDatabase, otherwise
  // the delete blocks indefinitely waiting for all connections to close.
  if (cachedDb) {
    cachedDb.close();
    cachedDb = null;
  }
  cachedDbPromise = null;
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () => resolve(); // best-effort
  });
}

/** Test seam — drops the entire DB. Vitest tests call this between cases. */
export async function __resetForTesting(): Promise<void> {
  await deleteQueueDatabase();
}
